import type { DateValue } from '@internationalized/date'

import DividerWithText from '@frontend/components/DividerWithText'
import { DatePicker, Input, PasswordInput } from '@frontend/components/form'
import { getLocalTimeZone, today } from '@internationalized/date'
import { Button } from '@nextui-org/react'
import { INPUT_VARIANT } from '@shared/constant'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import type { RegisterState } from './lib/type'

import {
  EMAIL_REGEX,
  PASSWORD_REGEX,
  PASSWORD_REGEX_MESSAGE,
} from './lib/constraint'
import { useRegister } from './lib/hook'
import SocialLogin from './SocialLogin'

export function SignUpForm() {
  const { control, handleSubmit } = useForm<RegisterState>({
    mode: 'onTouched',
  })

  const { isLoading, mutate } = useRegister()

  const onSubmit = useCallback(
    async (data: RegisterState) => {
      const birthDate = data.birthDate as unknown as DateValue
      const values = {
        ...data,
        birthDate: birthDate.toString(),
      }

      await toast.promise(mutate(values), {
        error: (error) =>
          error?.message ||
          "Couldn't create your account, please try again later.",
        loading: 'Creating your account...',
        success: (response) => {
          window.location.href = '/'
          return response?.message || 'Forgot password successfully'
        },
      })
    },
    [mutate],
  )

  return (
    <>
      <h2 className="mb-10 text-center font-medium uppercase">New customers</h2>
      <form className="relative space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Input
            control={control}
            fieldProps={{
              label: 'First Name',
              variant: INPUT_VARIANT,
            }}
            name="firstName"
            rules={{
              required: 'Please enter your first name',
            }}
          />
          <Input
            control={control}
            fieldProps={{
              label: 'Last Name',
              variant: INPUT_VARIANT,
            }}
            name="lastName"
            rules={{
              required: 'Please enter your last name',
            }}
          />
          <DatePicker
            control={control}
            fieldProps={{
              label: 'Birth Date',
              maxValue: today(getLocalTimeZone()),
              showMonthAndYearPickers: true,
              variant: INPUT_VARIANT,
            }}
            name="birthDate"
            rules={{
              required: 'Please enter your birth date',
            }}
          />
          <Input
            control={control}
            fieldProps={{
              inputMode: 'tel',
              label: 'Phone Number',
              variant: INPUT_VARIANT,
            }}
            name="phoneNumber"
            rules={{
              required: 'Please enter your phone number',
            }}
          />
        </div>
        <Input
          control={control}
          fieldProps={{
            inputMode: 'email',
            label: 'Email',
            variant: INPUT_VARIANT,
          }}
          name="email"
          rules={{
            pattern: {
              message: 'Please enter a valid email address.',
              value: EMAIL_REGEX,
            },
            required: 'Please enter your email',
          }}
        />
        <PasswordInput
          control={control}
          fieldProps={{
            description:
              'Minimum 8 characters, at least one uppercase letter, one lowercase letter, one number and one symbol.',
            label: 'Password',
            variant: INPUT_VARIANT,
          }}
          name="password"
          rules={{
            pattern: {
              message: PASSWORD_REGEX_MESSAGE,
              value: PASSWORD_REGEX,
            },
            required: 'Please enter your password',
          }}
        />
        <Button
          className="!mt-10 font-medium"
          color="primary"
          fullWidth
          isLoading={isLoading}
          size="lg"
          type="submit"
        >
          Sign up
        </Button>
      </form>
      <small className="mt-6 block text-gray-500">
        By creating an account, you confirm your agreement with our{' '}
        <a
          className="text-primary"
          href="/term-conditions/"
          rel="noreferrer"
          target="_blank"
        >
          Terms and Conditions
        </a>{' '}
        and{' '}
        <a
          className="text-primary"
          href="/privacy-policy/"
          rel="noreferrer"
          target="_blank"
        >
          Privacy Policy
        </a>{' '}
        . By proceeding with the sign-up, I acknowledge that I have thoroughly
        reviewed and consented to ASAVA&apos;s Terms of Use and Privacy Policy.
        This document details how ASAVA collects, uses, and discloses my
        personal data, as well as the rights I am afforded under applicable law.
      </small>
      <DividerWithText className="my-8" text="OR" />
      <SocialLogin />
    </>
  )
}

export default SignUpForm
